import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronEndIcon } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { locales } from "@/lib/i18n/config";
import { endpoints } from "@/lib/api/endpoints";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { getSellerLoginUrl } from "@/lib/auth/storeSlug";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useStoreSwitch } from "@/shared/services/useStoreSwitch";
import { LogoutButton } from "@/shared/layout/LogoutButton";
import { usePwaInstall, openInAndroidBrowser } from "@/modules/pwa/components/usePwaInstall";
import { usePageChrome } from "@/shared/layout/PageChromeContext";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { PageTransition } from "@/shared/components/PageTransition";
import { MobileScreenHeader } from "@/shared/components/MobileScreenHeader";
import { useThemeStore, type ThemePref } from "@/shared/theme/useTheme";
import { PushStatusRow } from "@/modules/notifications/components/PushStatusRow";
import { MobileSettingsList } from "@/modules/account/components/MobileSettingsList";
import { Combobox } from "@a2z/ui";

type AccountPageProps = {
  locale: string;
  storeSlug: string;
};

type MenuItem = {
  id: string;
  href: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  showHint: boolean;
};

export function AccountPage({ locale, storeSlug }: AccountPageProps) {
  const t = useTranslations();
  const isMobile = useIsMobile();
  const { setHero, setHeroAux } = usePageChrome();
  const { data: sessionData } = useSessionQuery({ storeSlug });
  const pwa = usePwaInstall();
  const {
    isSwitching: isSwitchingStore,
    error: switchStoreError,
    switchTo: handleStoreSwitch,
  } = useStoreSwitch(locale, storeSlug);

  const storeName = sessionData?.seller.storeName ?? storeSlug;
  const storeShort = sessionData?.seller.storeShort ?? null;
  const availableStores = sessionData?.availableStores ?? [];
  const canSwitchStores = availableStores.length > 1;
  const loginUrl = getSellerLoginUrl(locale);

  const [copied, setCopied] = useState(false);

  const copyStoreId = useCallback(() => {
    const id = storeShort ?? storeSlug;
    try {
      void navigator.clipboard?.writeText(id);
    } catch {
      // Clipboard API may be unavailable in some browsers — ignore.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  }, [storeShort, storeSlug]);

  // Grouped to mirror the desktop sidebar taxonomy (roundtable verdict) and to
  // give the Store Editor a mobile home (previously orphaned — see
  // nav-ia-parked-bugs).
  const menuGroups: { header: string; items: MenuItem[] }[] = [
    {
      // Customers is a top-level sidebar item on desktop but has no bottom-nav
      // tab on mobile — the Account hub is its mobile home.
      header: t(keys.seller.navGroupStore),
      items: [
        {
          id: "customers",
          href: getSellerPath(locale, storeSlug, "/customers"),
          label: t(keys.customers.pageTitle),
          description: t(keys.seller.accountMenuCustomersDesc),
          icon: <CustomersIcon />,
          showHint: false,
        },
      ],
    },
    {
      header: t(keys.seller.navGroupCatalog),
      items: [
        // Categories moved to a segment under the Products tab — see
        // CatalogMobileHeader. It's no longer surfaced in the Account hub.
        {
          id: "coupons",
          href: getSellerPath(locale, storeSlug, "/coupons"),
          label: t(keys.coupons.pageTitle),
          description: t(keys.seller.accountMenuCouponsDesc),
          icon: <CouponIcon />,
          showHint: false,
        },
        {
          id: "reviews",
          href: getSellerPath(locale, storeSlug, "/reviews"),
          label: t(keys.reviews.navLabel),
          description: t(keys.seller.accountMenuReviewsDesc),
          icon: <ReviewsIcon />,
          showHint: false,
        },
      ],
    },
    {
      header: t(keys.seller.navGroupChannels),
      items: [
        {
          id: "store-editor",
          href: getSellerPath(locale, storeSlug, "/store/editor"),
          label: t(keys.seller.storeEditorTitle),
          description: t(keys.seller.accountMenuEditorDesc),
          icon: <EditorIcon />,
          showHint: false,
        },
        {
          id: "store-editor-simple",
          href: getSellerPath(locale, storeSlug, "/store/editor/simple"),
          label: t(keys.seller.storeEditorSimpleTitle),
          description: t(keys.seller.accountMenuEditorSimpleDesc),
          icon: <EditorIcon />,
          showHint: false,
        },
      ],
    },
    {
      header: t(keys.seller.navGroupAccount),
      items: [
        {
          id: "settings",
          href: getSellerPath(locale, storeSlug, "/settings"),
          label: t(keys.seller.storeSettingsTitle),
          description: t(keys.seller.accountMenuSettingsDesc),
          icon: <SettingsIcon />,
          showHint: false,
        },
        {
          id: "profile",
          href: getSellerPath(locale, storeSlug, "/profile"),
          label: t(keys.seller.accountTitle),
          description: t(keys.seller.accountMenuProfileDesc),
          icon: <ProfileIcon />,
          showHint: false,
        },
      ],
    },
  ];

  const memberSinceLabel = t(keys.seller.accountMemberSince);
  const copyIdLabel = t(keys.seller.accountCopyId);
  const copiedLabel = t(keys.seller.accountCopiedId);
  const storeIdDisplay = storeShort ?? storeSlug;

  // Desktop hero — dark navy chrome with monogram + identity + Copy ID.
  // Skipped on mobile (we render a light card inline instead).
  useEffect(() => {
    if (isMobile) {
      setHero(null);
      return () => setHero(null);
    }
    setHero(
      <div className="flex flex-wrap items-center gap-5">
        <Monogram size={64} storeName={storeName} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="m-0 truncate text-2xl font-semibold leading-tight tracking-[-0.012em] text-[color:var(--text-primary)]">
            {storeName}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-[13px] text-[color:var(--text-muted)]">
            <span className="font-mono tracking-[0.04em]">{storeIdDisplay}</span>
            <span
              aria-hidden="true"
              className="inline-block h-[3px] w-[3px] rounded-full bg-white/30"
            />
            <span>
              {memberSinceLabel}{" "}
              {formatMemberSince(
                sessionData?.seller.createdAt ?? null,
                locale,
              )}
            </span>
          </div>
        </div>
        <CopyButton
          copied={copied}
          copyLabel={copyIdLabel}
          copiedLabel={copiedLabel}
          onClick={copyStoreId}
        />
      </div>,
    );
    return () => setHero(null);
  }, [
    setHero,
    isMobile,
    storeName,
    storeIdDisplay,
    memberSinceLabel,
    sessionData?.seller.createdAt,
    locale,
    copied,
    copyIdLabel,
    copiedLabel,
    copyStoreId,
  ]);

  useEffect(() => {
    setHeroAux(null);
    return () => setHeroAux(null);
  }, [setHeroAux]);

  return (
    <>
    {/* Standard light large-title header — same chrome as the other primary
        screens (Catalog/Orders). Search lives here; the bell is appended by
        the header itself. */}
    {isMobile ? (
      <MobileScreenHeader
        theme="light"
        surface="var(--shell)"
        title={t(keys.seller.navGroupAccount)}
        ariaLabel={t(keys.seller.navGroupAccount)}
        heading={
          <h1 className="m-0 text-[23px] font-bold leading-tight tracking-[-0.025em] text-[color:var(--text-primary)]">
            {t(keys.seller.navGroupAccount)}
          </h1>
        }
      />
    ) : null}
    <PageTransition className="w-full">
      <div
        className={[
          "mx-auto flex max-w-[1080px] flex-col gap-6",
          isMobile ? "px-4 pb-10 pt-3.5" : "px-7 pb-20 pt-6",
        ].join(" ")}
      >
        {isMobile ? (
          /* Mobile: the merged Settings screen as a flat grouped list. */
          <MobileSettingsList
            locale={locale}
            storeSlug={storeSlug}
            storeName={storeName}
            availableStores={availableStores}
            isSwitchingStore={isSwitchingStore}
            onSwitchStore={handleStoreSwitch}
            loginUrl={loginUrl}
          />
        ) : (
          <>
            {/* Desktop store switcher */}
            {canSwitchStores ? (
              <section className="rounded-[14px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4">
                <label
                  htmlFor="account-store-switch"
                  className="text-[13px] font-semibold text-[color:var(--text-secondary)]"
                >
                  {t(keys.seller.storeSwitcherLabel)}
                </label>
                <div className="mt-2">
                  <Combobox
                    options={availableStores.map((store) => ({
                      value: store.storeSlug,
                      label: store.storeName,
                    }))}
                    value={storeSlug}
                    onChange={(value) => {
                      if (value) void handleStoreSwitch(value);
                    }}
                  />
                </div>
                {isSwitchingStore ? (
                  <p className="mt-2 text-[12.5px] text-[color:var(--text-muted)]">
                    {t(keys.seller.storeSwitcherLoading)}
                  </p>
                ) : null}
                {switchStoreError ? (
                  <p className="mt-2 text-[12.5px] text-[color:var(--status-error)]">
                    {switchStoreError}
                  </p>
                ) : null}
              </section>
            ) : null}

            {/* Manage — grouped grids mirroring the desktop sidebar taxonomy */}
            {menuGroups.map((group) => (
              <section key={group.header}>
                <SectionLabel>{group.header}</SectionLabel>
                <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {group.items.map((item) => (
                    <MenuCard
                      key={item.id}
                      href={item.href}
                      label={item.label}
                      description={item.description}
                      icon={item.icon}
                      showHint={item.showHint}
                      hintLabel={t(keys.seller.navHintSetup)}
                    />
                  ))}
                </div>
              </section>
            ))}

            {/* Notifications — push-registration status */}
            <section className="flex flex-col gap-3.5">
              <SectionLabel>{t(keys.notifications.title)}</SectionLabel>
              <PushStatusRow />
            </section>

            {/* Preferences — language picker, install hint, logout */}
            <section className="flex flex-col gap-3.5">
              <SectionLabel>
                {t(keys.seller.accountSectionPreferences)}
              </SectionLabel>

              <LanguagePicker currentLocale={locale} />

              <ThemePicker />

              {pwa.isReady &&
              !pwa.isInstalled &&
              (pwa.canInstall ||
                pwa.isIosSafari ||
                pwa.isIosNonSafari ||
                pwa.isAndroidManual ||
                pwa.isInAppBrowser) ? (
                <InstallBanner pwa={pwa} />
              ) : null}

              <LogoutButton
                endpoint={endpoints.seller.auth.logout}
                redirectTo={loginUrl}
                className="flex h-[48px] w-full items-center justify-center gap-2.5 rounded-[12px] border border-[color:var(--border-strong)] bg-[color:var(--surface)] px-4 text-[14.5px] font-semibold text-[color:var(--error-500)] transition-colors hover:bg-[color:var(--surface-alt)]"
              />

              <div className="mt-1 text-center text-[11.5px] text-[color:var(--text-muted)]">
                {t(keys.common.appName)} · {storeSlug} · v2.4.0
              </div>
            </section>
          </>
        )}
      </div>
    </PageTransition>

    </>
  );
}

// ─── Reusable bits ──────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-[color:var(--text-muted)]">
      {children}
    </div>
  );
}

function Monogram({
  size,
  storeName,
}: {
  size: number;
  storeName: string;
}) {
  const initial = (storeName || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      aria-hidden="true"
      className="flex shrink-0 items-center justify-center font-serif font-bold"
      style={{
        width: size,
        height: size,
        borderRadius: 14,
        background:
          "linear-gradient(135deg, var(--accent-500), #fbbf24)",
        color: "var(--text-primary)",
        fontSize: Math.round(size * 0.42),
        border:
          "1px solid color-mix(in oklab, var(--accent-500) 60%, black 10%)",
      }}
    >
      {initial}
    </div>
  );
}

function CopyButton({
  copied,
  copyLabel,
  copiedLabel,
  onClick,
  dark,
}: {
  copied: boolean;
  copyLabel: string;
  copiedLabel: string;
  onClick: () => void;
  dark?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={copyLabel}
      className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[10px] px-3 py-2 text-[12.5px] font-semibold transition-colors"
      style={
        dark
          ? {
              background: "rgba(255,255,255,0.08)",
              color: "#e8ecf4",
              border: "1px solid rgba(255,255,255,0.16)",
            }
          : {
              background: "var(--surface)",
              color: "var(--text-secondary)",
              border: "1px solid var(--border-strong)",
            }
      }
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
      {copied ? copiedLabel : copyLabel}
    </button>
  );
}

function MenuCard({
  href,
  label,
  description,
  icon,
  showHint,
  hintLabel,
}: {
  href: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  showHint: boolean;
  hintLabel: string;
}) {
  return (
    <Link
      to={href}
      className="group flex items-center gap-3.5 rounded-[14px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4 no-underline transition-colors hover:border-[color:var(--border-strong)]"
    >
      <span
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[11px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-secondary)] transition-colors group-hover:border-[color:color-mix(in_oklab,var(--accent-500)_24%,var(--surface))] group-hover:bg-[color:color-mix(in_oklab,var(--accent-500)_12%,var(--surface))] group-hover:text-[color:var(--accent-600)]"
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[14.5px] font-semibold text-[color:var(--text-primary)]">
          {label}
        </div>
        <div className="mt-0.5 truncate text-[12.5px] text-[color:var(--text-muted)]">
          {description}
        </div>
      </div>
      {showHint ? (
        <span className="inline-flex shrink-0 items-center rounded-full border border-[color:var(--border-light)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--text-muted)]">
          {hintLabel}
        </span>
      ) : null}
      <span
        aria-hidden="true"
        className="shrink-0 text-[color:var(--text-muted)] transition-transform group-hover:translate-x-0.5"
      >
        <ChevronEndIcon />
      </span>
    </Link>
  );
}

function ThemePicker() {
  const t = useTranslations();
  const pref = useThemeStore((s) => s.pref);
  const setPref = useThemeStore((s) => s.setPref);
  const options: { id: ThemePref; label: string }[] = [
    { id: "light", label: t(keys.common.themeLight) },
    { id: "dark", label: t(keys.common.themeDark) },
    { id: "system", label: t(keys.common.themeSystem) },
  ];

  return (
    <div className="flex flex-col gap-3 rounded-[14px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
      <div>
        <div className="text-[14px] font-semibold text-[color:var(--text-primary)]">
          {t(keys.common.theme)}
        </div>
        <div className="mt-0.5 text-[12.5px] text-[color:var(--text-muted)]">
          {t(keys.seller.accountThemeHint)}
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        {options.map((option) => {
          const active = pref === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => setPref(option.id)}
              aria-pressed={active}
              className="flex min-h-11 min-w-[76px] items-center justify-center rounded-[10px] px-4 py-2.5 text-[13.5px] font-semibold text-[color:var(--text-primary)] transition-colors"
              style={{
                background: active
                  ? "color-mix(in oklab, var(--accent-500) 6%, var(--surface))"
                  : "var(--surface)",
                border: active
                  ? "1.5px solid var(--accent-500)"
                  : "1px solid var(--border-strong)",
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LanguagePicker({ currentLocale }: { currentLocale: string }) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const options = [
    {
      id: "en",
      label: t(keys.common.languageEnglish),
      native: "English",
    },
    {
      id: "ar",
      label: t(keys.common.languageArabic),
      native: "العربية",
    },
  ];

  const handleChange = (next: string) => {
    if (next === currentLocale) return;
    const path = pathname ?? "/";
    const localePattern = new RegExp(`^/(${locales.join("|")})(/|$)`);
    const nextPath = localePattern.test(path)
      ? path.replace(localePattern, `/${next}$2`)
      : `/${next}${path.startsWith("/") ? "" : "/"}${path}`;
    navigate(nextPath, { replace: true });
  };

  return (
    <div className="flex flex-col gap-3 rounded-[14px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
      <div>
        <div className="text-[14px] font-semibold text-[color:var(--text-primary)]">
          {t(keys.common.language)}
        </div>
        <div className="mt-0.5 text-[12.5px] text-[color:var(--text-muted)]">
          {t(keys.seller.accountLanguageHint)}
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        {options.map((option) => {
          const active = currentLocale === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => handleChange(option.id)}
              className="flex min-w-[96px] flex-col items-start gap-px rounded-[10px] px-4 py-2.5 transition-colors"
              style={{
                background: active
                  ? "color-mix(in oklab, var(--accent-500) 6%, var(--surface))"
                  : "var(--surface)",
                border: active
                  ? "1.5px solid var(--accent-500)"
                  : "1px solid var(--border-strong)",
              }}
            >
              <span className="text-[13.5px] font-semibold text-[color:var(--text-primary)]">
                {option.label}
              </span>
              <span
                /* --text-muted sits at 4.27:1 on the *selected* chip's tinted
                   background — just under AA. Secondary clears it. */
                className="text-[11.5px] text-[color:var(--text-secondary)]"
                style={{
                  fontFamily:
                    option.id === "ar"
                      ? "'IBM Plex Sans Arabic', system-ui, sans-serif"
                      : "inherit",
                }}
              >
                {option.native}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function InstallBanner({ pwa }: { pwa: ReturnType<typeof usePwaInstall> }) {
  const t = useTranslations();
  if (pwa.canInstall) {
    return (
      <button
        type="button"
        onClick={() => void pwa.install()}
        className="flex w-full items-center gap-3.5 rounded-[14px] p-4 text-start transition-colors"
        style={{
          background: "color-mix(in oklab, var(--accent-500) 5%, var(--surface))",
          border: "1px solid color-mix(in oklab, var(--accent-500) 20%, var(--surface))",
        }}
      >
        <InstallTile />
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-[color:var(--text-primary)]">
            {t(keys.seller.pwaInstallAction)}
          </div>
          <div className="mt-0.5 text-[12.5px] leading-snug text-[color:var(--text-secondary)]">
            {t(keys.seller.pwaInstallHint)}
          </div>
        </div>
        <ChevronEndIcon />
      </button>
    );
  }
  if (pwa.isIosNonSafari) {
    return (
      <button
        type="button"
        onClick={() => {
          const url = window.location.href;
          window.location.href = url.replace(/^https?:\/\//, (s) =>
            s === "https://" ? "x-safari-https://" : "x-safari-http://",
          );
        }}
        className="flex w-full items-center gap-3.5 rounded-[14px] p-4 text-start transition-colors"
        style={{
          background: "color-mix(in oklab, var(--accent-500) 5%, var(--surface))",
          border: "1px solid color-mix(in oklab, var(--accent-500) 20%, var(--surface))",
        }}
      >
        <InstallTile />
        <div className="min-w-0 flex-1 text-[13.5px] font-semibold text-[color:var(--text-primary)]">
          {t(keys.seller.pwaOpenInSafariAction)}
        </div>
        <ChevronEndIcon />
      </button>
    );
  }
  if (pwa.isInAppBrowser) {
    return (
      <button
        type="button"
        onClick={openInAndroidBrowser}
        className="flex w-full items-center gap-3.5 rounded-[14px] p-4 text-start transition-colors"
        style={{
          background: "color-mix(in oklab, var(--accent-500) 5%, var(--surface))",
          border: "1px solid color-mix(in oklab, var(--accent-500) 20%, var(--surface))",
        }}
      >
        <InstallTile />
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-[color:var(--text-primary)]">
            {t(keys.seller.pwaOpenInBrowserAction)}
          </div>
          <div className="mt-0.5 text-[12.5px] leading-snug text-[color:var(--text-secondary)]">
            {t(keys.seller.pwaInAppBrowserBody)}
          </div>
        </div>
        <ChevronEndIcon />
      </button>
    );
  }
  const instructionBody = pwa.isAndroidManual
    ? t(keys.seller.pwaAndroidManualBody)
    : t(keys.seller.pwaModalIosBody);
  return (
    <div
      className="flex items-center gap-3.5 rounded-[14px] p-4"
      style={{
        background: "color-mix(in oklab, var(--accent-500) 5%, var(--surface))",
        border: "1px solid color-mix(in oklab, var(--accent-500) 20%, var(--surface))",
      }}
    >
      <InstallTile />
      <p className="text-[12.5px] leading-snug text-[color:var(--text-secondary)]">
        {instructionBody}
      </p>
    </div>
  );
}

function InstallTile() {
  return (
    <span
      className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px]"
      style={{
        background: "color-mix(in oklab, var(--accent-500) 14%, var(--surface))",
        color: "var(--accent-600)",
        border: "1px solid color-mix(in oklab, var(--accent-500) 25%, var(--surface))",
      }}
    >
      <DownloadIcon />
    </span>
  );
}

function formatMemberSince(iso: string | null, locale: string): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-US", {
    month: "short",
    year: "numeric",
  }).format(date);
}

// ─── Icons ──────────────────────────────────────────────────────────────────

function ProfileIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function CouponIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}

function CustomersIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.3 2.9-5.5 6.5-5.5s6.5 2.2 6.5 5.5" />
      <path d="M16 4.8a3.5 3.5 0 0 1 0 6.4" />
      <path d="M18.2 15.1c2 .8 3.3 2.6 3.3 4.9" />
    </svg>
  );
}

function ReviewsIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2l3 6.6 7.3.7-5.5 4.9 1.6 7.1L12 17.8 5.6 21.3l1.6-7.1L1.7 9.3l7.3-.7L12 2z" />
    </svg>
  );
}

function EditorIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12" />
      <path d="M7 11l5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
}
